import { captureExceptionWithTags } from 'src/utils/sentry';

import { analyticsClient } from './client';

type BaseEvent = {
  attributes?: object;
  containerType?: string;
  containerId?: string;
  objectType?: string;
  objectId?: string;
  tags?: string[];
  containers?: {
    [key: string]: {
      id?: string;
      type?: string;
    };
  };
};

type Event = {
  action: string;
  actionSubject: string;
  actionSubjectId?: string;
  source: string;
} & BaseEvent;

export type TrackEvent = Event;
export type UiEvent = Event;
export type OperationalEvent = Event;

export type ScreenEvent = {
  name: string;
} & BaseEvent;

// Utility type for creating similar UI events.
// Useful for filters where the events generally
// share multiple properties.
export type FilterEventMap<T> = {
  [K in keyof T]: (value?: T[keyof T]) => Event;
};

const hydrateAnalyticsEventAttributes = (attributes?: object | null) => ({
  ...attributes,
  frontbucketView: true,
});

/** Screen Events: when a user looks at a screen */
const publishScreenEvent = (event: string, attributes?: object | null) => {
  try {
    analyticsClient().sendScreenEvent({
      name: event,
      attributes: hydrateAnalyticsEventAttributes(attributes),
    });
  } catch (e) {
    // Log exception to Sentry and continue execution.
    captureExceptionWithTags(e, { component: 'analytics' });
  }
};

/** Full Screen Events: adds additional context to ScreenEvents */
const publishFullScreenEvent = (event: ScreenEvent) => {
  try {
    analyticsClient().sendScreenEvent({
      ...event,
      attributes: hydrateAnalyticsEventAttributes(event.attributes),
    });
  } catch (e) {
    // Log exception to Sentry and continue execution.
    captureExceptionWithTags(e, { component: 'analytics' });
  }
};

/** Track Events: when a user performs a product action (created issue, deleted project) */
const publishTrackEvent = (event: TrackEvent) => {
  try {
    analyticsClient().sendTrackEvent({
      ...event,
      attributes: hydrateAnalyticsEventAttributes(event.attributes),
    });
  } catch (e) {
    // Log exception to Sentry and continue execution.
    captureExceptionWithTags(e, { component: 'analytics' });
  }
};

/** UI Events: when a user interacts with the UI (clicks a button, selects an option in a dropdown) */
const publishUiEvent = (event: UiEvent, onEventSent?: () => void) => {
  try {
    const eventWithAttributes = {
      ...event,
      attributes: hydrateAnalyticsEventAttributes(event.attributes),
    };
    if (onEventSent) {
      analyticsClient().sendUIEvent(eventWithAttributes, onEventSent);
    } else {
      analyticsClient().sendUIEvent(eventWithAttributes);
    }
  } catch (e) {
    // Log exception to Sentry and continue execution.
    captureExceptionWithTags(e, { component: 'analytics' });
  }
};

const publishOperationalEvent = (event: OperationalEvent) => {
  try {
    analyticsClient().sendOperationalEvent({
      ...event,
      attributes: hydrateAnalyticsEventAttributes(event.attributes),
    });
  } catch (e) {
    // Log exception to Sentry and continue execution.
    captureExceptionWithTags(e, { component: 'analytics' });
  }
};

export {
  publishScreenEvent,
  publishFullScreenEvent,
  publishTrackEvent,
  publishUiEvent,
  publishOperationalEvent,
};
